import React, { useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import ViewButtonComponent from "../../../../../../components/ViewButtonComponent";
import TableLoadingComponent from "../../../../../../components/TableLoadingComponent";
import { useSelector } from "react-redux";
import { useFirebaseCartSummaries } from "../../../../../../hooks/useFirebaseCartSummaries";

interface CartProductSummary {
  id: string;
  displayName: string;
  productId: string;
  itemId: string;
  totalAvailable: number;
  totalReserved: number;
  totalAllocated: number;
  totalSold: number;
  totalReceived: number;
  channels: {
    [channelName: string]: {
      available: number;
      reserved: number;
      allocated: number;
      sold: number;
      received: number; 
    };
  };
}

interface InventoryTableProps {
  selectedDate: string;
  cartId: string;
  cartName?: string;
}

const InventoryTable: React.FC<InventoryTableProps> = ({ 
  selectedDate,
  cartId,
  cartName: propCartName
}) => {
  const navigate = useNavigate();
  
  // Get restaurant ID from Redux
  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.id,
  );

  // Convert selectedDate string to Date object for the hook
  const dateObject = useMemo(() => new Date(selectedDate), [selectedDate]);
  const [cartName, setCartName] = useState(propCartName || '');

  // Use the cart summaries hook
  const { 
    data: cartSummaries, 
    loading: isLoading, 
    error 
  } = useFirebaseCartSummaries(restaurantId, dateObject);

  // Find the specific cart data
  const currentCart = useMemo(() => {
    if (!cartSummaries) return null;
    return cartSummaries.find(cart => cart.id === cartId);
  }, [cartSummaries, cartId]);

  // Process products from the cart for table display
  const cartProducts = useMemo<CartProductSummary[]>(() => {
    if (!currentCart || !currentCart.products) return [];

    return Object.values(currentCart.products)
      .filter(product => {
        // Filter out products where displayName equals productId
        const isDisplayNameValid = 
          product.displayName && 
          product.displayName.trim() !== '' &&
          product.displayName !== product.productId && 
          !product.displayName.match(/^[a-f0-9]{24}$/); 
        
        return isDisplayNameValid;
      })
      .map(product => {
        // Calculate totals across all channels for this product
        let totalAvailable = 0;
        let totalReserved = 0;
        let totalAllocated = 0;
        let totalSold = 0;
        let totalReceived = 0;
        
        const channelData: { [channelName: string]: any } = {};

        Object.entries(product.channels || {}).forEach(([channelName, channel]) => {
          const available = channel.available || 0;
          const reserved = channel.reserved || 0;
          const allocated = channel.allocated || 0;
          const sold = channel.sold || 0;
          const received = channel.received || 0; 

          totalAvailable += available;
          totalReserved += reserved;
          totalAllocated += allocated;
          totalSold += sold;
          totalReceived += received; 

          channelData[channelName] = {
            available,
            reserved,
            allocated,
            sold,
            received 
          };
        });

        return {
          id: product.id,
          displayName: product.displayName,
          productId: product.productId,
          itemId: product.itemId,
          totalAvailable,
          totalReserved,
          totalAllocated,
          totalSold,
          totalReceived,
          channels: channelData
        };
      });
  }, [currentCart]);

  useEffect(() => {
    if (currentCart) {
      setCartName(currentCart.cartName || '');
    } else if (propCartName) {
      setCartName(propCartName);
    }
  }, [currentCart, propCartName]);

  const handleView = (product: CartProductSummary) => {
    navigate("/carts/cart-list/view/inventory-view", {
      state: { 
        product,
        cartId,
        cartName: cartName,
        selectedDate 
      },
    });
  };

  const columns: TableColumn<CartProductSummary>[] = [
    {
      name: "Product Name",
      selector: (row) => row.displayName,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div>
          <div className="fw-medium">{row.displayName}</div>
          <div className="text-muted small">Product ID: {row.productId}</div>
        </div>
      ),
    },
    {
      name: "Total Available",
      selector: (row) => row.totalAvailable,
      sortable: true,
      cell: (row) => (
        <span className={`fw-bold ${row.totalAvailable <= 0 ? "text-danger" : "text-success"}`}>
          {row.totalAvailable}
        </span>
      ),
      width: "150px",
    },
    {
      name: "Total Reserved",
      selector: (row) => row.totalReserved,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <span className="text-warning fw-medium">{row.totalReserved}</span>
      ),
    },
    {
      name: "Total Allocated",
      selector: (row) => row.totalAllocated,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <span className="text-info fw-medium">{row.totalAllocated}</span>
      ),
    },
    {
      name: "Total Received",
      selector: (row) => row.totalReceived,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <span className="text-secondary fw-medium">{row.totalReceived}</span>
      ),
    },
    {
      name: "Total Sold",
      selector: (row) => row.totalSold,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <span className="text-primary fw-medium">{row.totalSold}</span>
      ),
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex gap-2">
          <ViewButtonComponent 
            onClick={() => handleView(row)} 
          />
        </div>
      ), 
      width: "100px",
      center: true,
    },
  ];

  if (error) {
    return (
      <div className="alert alert-danger mt-3">
        <h5>Error Loading Cart Inventory</h5>
        <p>{error.message}</p>
        <button 
          className="btn btn-sm btn-outline-danger"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="bg-white p-3 rounded-top border">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">Cart Inventory for {selectedDate}</h5>
          </div>
        </div>
      </div>
      <div className="border rounded-bottom">
        {isLoading ? (
          <div className="p-3">
            <TableLoadingComponent numColumns={8} /> {/* Updated column count */}
          </div>
        ) : cartProducts.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">
              {currentCart 
                ? `No inventory data available for ${currentCart.cartName || 'this cart'} on ${selectedDate}`
                : `No Cart Inventory found for the selected Cart.`
              }
            </p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={cartProducts}
            responsive
            pagination
            highlightOnHover
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            noHeader
            className="border-0"
            striped
          />
        )}
      </div>
    </div>
  );
};

export default InventoryTable;